//Built-in libraries

import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { deleteApplicationEvent } from '@/api/Rover/ApplicationEvents';
import Confirm from '@/components/UI/Modal/Confirm';
import type { ApplicationPhaseType, IApplicationEvent } from '@/types/ClientApplication';
import { RoverReactQueryKey } from '../../../../../../constants/ReactQueryKeys';
//Services
import { notifyUser } from '../../../../../../store/Toast';
//Assets andd Miscellaneous
import { formatDate3, sanitizeResponseError } from '../../../../../../utils';
//Custom components
import { ApplicationEventFormModal } from '../DueDiligenceEventFormModal';

function DueDiligenceEvent({ event, phase }: { event: IApplicationEvent; phase: ApplicationPhaseType }) {
  const { applicationId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [selectedApplicationEvent, setSelectedApplicationEvent] = useState<IApplicationEvent>();

  const toggleDeleteConfirmation = () => {
    setShowDeleteConfirmation((prev) => !prev);
  };

  const handleSelection = (event: IApplicationEvent) => {
    setSelectedApplicationEvent({
      ...event,
      date: formatDate3(event.date, 'yyyy-mm-dd'),
    });
  };

  const removeApplicationEvent = useMutation(
    () =>
      deleteApplicationEvent({
        applicationId: applicationId!,
        eventId: Number(selectedApplicationEvent?.id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_EVENTS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Application Event deleted successfully.',
            toastType: 'success',
          })
        );
        toggleDeleteConfirmation();
        setSelectedApplicationEvent(undefined);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleDeleteAccept = async () => {
    removeApplicationEvent.mutate();
  };

  const handleDeleteDecline = () => {
    toggleDeleteConfirmation();
    setSelectedApplicationEvent(undefined);
  };

  const handleApplicationEventModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleApplicationEventEdit = (event: IApplicationEvent) => {
    handleSelection(event);
    handleApplicationEventModal();
  };

  const handleApplicationEventDelete = (event: IApplicationEvent) => {
    handleSelection(event);
    toggleDeleteConfirmation();
  };

  return (
    <>
      <div className='flex justify-between w-full px-2 py-4'>
        <span className='w-3 h-3 bg-red-400 mr-2 rounded-full mt-0.5'></span>
        <div className='flex justify-between w-full pl-2'>
          <div>
            <p className='text-sm'>{event.name}</p>
            <p className='text-gray-900 font-semibold break-words w-80'>{formatDate3(event.date, 'yyyy-mm-dd')}</p>
          </div>
          <div className='flex gap-2 '>
            <div
              className='hover:bg-neutralColor-lightest text-neutralColor-normal flex justify-center items-center h-8 w-8 p-2 rounded-full hover:text-primaryColor-normal cursor-pointer transition duration-300 ease-in-out'
              onClick={() => handleApplicationEventEdit(event)}
            >
              <PencilIcon className='h-5 w-5' />
            </div>

            <div
              className='hover:bg-neutralColor-lightest text-neutralColor-normal flex justify-center items-center h-8 w-8 p-2 rounded-full hover:text-tertiaryColor-normal cursor-pointer transition duration-300 ease-in-out'
              onClick={() => handleApplicationEventDelete(event)}
            >
              <TrashIcon className='h-5 w-5' />
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <ApplicationEventFormModal
          handleModal={handleApplicationEventModal}
          hookFormConfig={{
            defaultValues: {
              name: '',
              date: '',
              ...selectedApplicationEvent,
            },
          }}
          title={`${phase} Event`}
          phase={phase}
        />
      )}
      {showDeleteConfirmation && (
        <Confirm
          title={`Remove ${selectedApplicationEvent?.name}`}
          message={`You are about to remove ${selectedApplicationEvent?.name} event. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={handleDeleteAccept}
          decline={handleDeleteDecline}
        />
      )}
    </>
  );
}
export default DueDiligenceEvent;
